import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './toolTip.scss';

/**
 * Custom Tooltip Component (Portal Version)
 * Matches Tooltip1 props and behavior but uses React Portal for better rendering
 *
 * @param {string} target - ID of the element to attach tooltip to
 * @param {string} placement - Position of tooltip: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {ReactNode} children - Tooltip content
 */
const Tooltip = ({ target, placement = 'top', children }) => {
    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);

    useEffect(() => {
        const el = document.getElementById(target);
        if (!el) return;

        const show = () => {
            setVisible(true);
            updatePosition(el);
        };

        const hide = () => setVisible(false);

        const updatePosition = (element) => {
            const rect = element.getBoundingClientRect();
            const tooltipEl = tooltipRef.current;
            if (!tooltipEl) return;

            const tooltipRect = tooltipEl.getBoundingClientRect();
            let top = 0;
            let left = 0;

            switch (placement) {
                case 'bottom':
                    top = rect.bottom + window.scrollY + 8;
                    left = rect.left + window.scrollX + rect.width / 2 - tooltipRect.width / 2;
                    break;
                case 'top':
                    top = rect.top + window.scrollY - tooltipRect.height - 8;
                    left = rect.left + window.scrollX + rect.width / 2 - tooltipRect.width / 2;
                    break;
                case 'left':
                    top = rect.top + window.scrollY + rect.height / 2 - tooltipRect.height / 2;
                    left = rect.left + window.scrollX - tooltipRect.width - 8;
                    break;
                case 'right':
                    top = rect.top + window.scrollY + rect.height / 2 - tooltipRect.height / 2;
                    left = rect.right + window.scrollX + 8;
                    break;
                default:
                    top = rect.top + window.scrollY - tooltipRect.height - 8;
                    left = rect.left + window.scrollX + rect.width / 2 - tooltipRect.width / 2;
            }

            setPos({ top, left });
        };

        el.addEventListener('mouseenter', show);
        el.addEventListener('mouseleave', hide);
        el.addEventListener('focus', show);
        el.addEventListener('blur', hide);

        return () => {
            el.removeEventListener('mouseenter', show);
            el.removeEventListener('mouseleave', hide);
            el.removeEventListener('focus', show);
            el.removeEventListener('blur', hide);
        };
    }, [target, placement]);

    if (!visible) return null;

    return ReactDOM.createPortal(
        <div
            ref={tooltipRef}
            className={`tooltip tooltip--${placement}`}
            style={{
                position: 'absolute',
                top: `${pos.top}px`,
                left: `${pos.left}px`,
                zIndex: 9999,
            }}
            role="tooltip"
        >
            <div className="tooltip__content">{children}</div>
            <div className={`tooltip__arrow tooltip__arrow--${placement}`} />
        </div>,
        document.body
    );
};

export default Tooltip;
